import React from 'react'
import { Crown, Lock, Trophy, XCircle } from 'lucide-react'
import BracketPlayerNames from './BracketPlayerNames'

/**
 * A single team row inside a bracket match card.
 *
 * Props:
 *  - team: team object with { seed, players }
 *  - teamSize: 1 for singles, 2 for doubles
 *  - teamId: the team ID occupying this slot (user's pick or actual)
 *  - isSelected: user picked this team as the winner of the current match
 *  - isPickCorrect: the user's pick for this match was correct
 *  - isPickIncorrect: the user's pick for this match was wrong
 *  - fromIncorrectPick: this team arrived via an incorrect pick in a previous round
 *  - eliminatedEarlier: this team was eliminated in a round before the previous one
 *  - isChampion: this team is the user's champion pick
 *  - isChampionCorrect: champion was predicted AND won — triggers neon gradient on every cell the champion appeared in
 *  - isLoserNotPicked: team exists but wasn't picked and lost (actual contender that lost)
 *  - isWinner: team won this match (for correct styling)
 *  - hasOfficialWinner: whether this match has an official result
 *  - officialWinnerId: the official winner's team ID (for CheckCircle on non-selected actual winners)
 *  - championTeamId: the user's champion team ID
 *  - showBorderB: whether to show a bottom border
 *  - canEditPicks: whether picks are editable (affects cursor styling)
 *  - onClick: click handler
 *  - actualWinnerShown: a BracketActualWinnerCell is already visible for this match (suppresses redundant CheckCircle)
 *  - isViewingOfficial: whether viewing the official bracket
 */
export default function BracketPickCell({
  team,
  teamSize = 1,
  teamId,
  isSelected = false,
  isPickCorrect = false,
  isPickIncorrect = false,
  fromIncorrectPick = false,
  eliminatedEarlier = false,
  isChampion = false,
  isChampionCorrect = false,
  isLoserNotPicked = false,
  isWinner = false,
  hasOfficialWinner = false,
  officialWinnerId,
  championTeamId,
  showBorderB = true,
  canEditPicks = false,
  onClick,
  actualWinnerShown = false,
  isViewingOfficial = false,
  // DUB-40: when the match IS the championship final and this cell's team is
  // the official champion, show a Crown regardless of whether the user picked
  // them. The previous indicator (a small mauve dot) was too easy to miss.
  isOfficialChampion = false,
  // Ticket 5dacdb9f: viewing another player's entry while this cell's round is
  // still open — their pick exists but is masked, so say so instead of
  // rendering a blank row that reads as "made no picks".
  hiddenPick = false,
}) {
  if (hiddenPick) {
    return (
      <div
        className={`w-full pl-3 pr-5 py-2.5 text-left overflow-hidden relative flex-1 basis-0 border-t-0 bg-background cursor-not-allowed ${showBorderB ? 'border-b border-[var(--color-bracket-card-border)]' : ''}`}
      >
        <div className="flex items-center gap-2 overflow-hidden text-muted-foreground">
          <Lock className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="text-sm italic truncate">Hidden until picks close</span>
        </div>
      </div>
    )
  }

  const isEliminated = fromIncorrectPick || eliminatedEarlier

  const winnerClass = isChampionCorrect ? 'bracket-pick-champion' : 'bracket-pick-winner'

  const cellClass = !teamId
    ? 'bg-background cursor-not-allowed'
    : isEliminated
    ? 'bg-background cursor-not-allowed'
    : isLoserNotPicked
    ? 'bg-background cursor-not-allowed'
    : isWinner
    ? `${winnerClass} ${!canEditPicks ? 'cursor-default' : ''}`
    : isSelected
    ? isPickIncorrect
      ? `bracket-pick-wrong ${!canEditPicks ? 'cursor-default' : ''}`
      : isViewingOfficial
      ? `bg-background font-semibold ${!canEditPicks ? 'cursor-default' : ''}`
      : `bracket-pick-predicted ${!canEditPicks ? 'cursor-default' : ''}`
    : canEditPicks
    ? 'bracket-pick-selectable'
    : 'bg-background'

  const isWinnerNotChampion = isWinner && !isChampionCorrect

  // DUB (2a): the official winner the user did NOT pick (e.g. round-1, where
  // both real teams share the card). It already shows a grey dot; bold the name
  // too so it matches the actual-winner cell used in later rounds.
  const isOfficialWinnerNotPicked = !isSelected && hasOfficialWinner && officialWinnerId === teamId

  const seedClass = isEliminated
    ? 'line-through text-[var(--color-pick-wrong-text)]'
    : (isSelected && isPickIncorrect)
    ? 'line-through'
    : isWinnerNotChampion
    ? 'text-[var(--color-pick-correct-text)]'
    : 'opacity-60'

  const nameClass = !teamId
    ? 'italic text-muted-foreground'
    : isEliminated
    ? 'line-through text-[var(--color-pick-wrong-text)]'
    : (isSelected && isPickIncorrect)
    ? 'line-through'
    : isWinnerNotChampion
    ? 'font-semibold text-[var(--color-pick-correct-text)]'
    : isOfficialWinnerNotPicked
    ? 'font-semibold'
    : ''

  const isChampionPickCell = isSelected && !!championTeamId && championTeamId === teamId
  const isChampionDead = isChampionPickCell && (isPickIncorrect || isEliminated)

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={!teamId || !canEditPicks ? -1 : 0}
      onClick={onClick}
      className={`group w-full pl-3 pr-5 py-2.5 transition-all text-left overflow-hidden relative flex-1 basis-0 border-t-0 ${showBorderB ? 'border-b border-[var(--color-bracket-card-border)]' : ''} ${cellClass}`}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        {team?.seed && (
          <span className={`text-xs font-semibold w-6 flex-shrink-0 ${seedClass}`}>
            {/* DUB-134: consistent seed format across pool + single-elim — no `#`. */}
            {team.seed}
          </span>
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          <BracketPlayerNames team={team} className={nameClass} teamSize={teamSize} />
        </div>
        {/* Trophy = the tournament champion (winner of the final), shown on the
            official-champion cell regardless of who the user picked; also the
            per-match "you got this right" trophy on a correct pick. */}
        {((isSelected && isPickCorrect) || isOfficialChampion) && (
          <Trophy className="h-4 w-4 flex-shrink-0 text-[var(--color-crown)]" />
        )}
        {(isSelected && isPickIncorrect || isEliminated) && (
          <XCircle className="h-4 w-4 flex-shrink-0 text-[var(--color-pick-wrong-text)]" />
        )}
        {!isSelected && hasOfficialWinner && officialWinnerId === teamId && !isOfficialChampion && (
          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[var(--mauve-8)]" />
        )}
        {/* Crown = the user's OWN champion pick (owner 2026-06-06): show it only
            on the team the user picked to win it all. Reverses DUB-40, which
            forced a Crown onto the official champion even when the user didn't
            pick them. Tickets f6d5885c / faf16b6a: keep the Crown on every round
            the champion pick appears in, even after it is knocked out — turned
            red to match the strikethrough and ✗ — so a player can still tell
            which dead pick was their champion (especially in the Championship
            card) without scrolling back through earlier rounds. */}
        {isChampionPickCell && (
          <Crown
            data-champion-dead={isChampionDead ? 'true' : undefined}
            className={`h-4 w-4 flex-shrink-0 ${isChampionDead ? 'text-[var(--color-pick-wrong-text)]' : 'text-[var(--color-crown)]'}`}
          />
        )}
      </div>
    </div>
  )
}
